import { FormEvent, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Loading, Modal, TextInput } from '@carbon/react';

import sharedMessages from 'Components/utils/sharedMessages';
import { useUserSelfAddToEntityMutation } from 'api/entityEndpoints';
import styles from './JoinExistingParishModal.module.scss';

type Props = {
    open: boolean;
    onClose: () => void;
    onJoined?: () => void;
};

const JoinExistingParishModal = ({
    open,
    onClose,
    onJoined,
}: Props) => {
    const intl = useIntl();
    const [invitationToken, setInvitationToken] = useState('');
    const [selfAdd, { isLoading }] = useUserSelfAddToEntityMutation();

    useEffect(() => {
        if (!open) {
            setInvitationToken('');
        }
    }, [open]);

    const trimmedToken = invitationToken.trim();

    const handleSubmit = async () => {
        if (!trimmedToken) {
            return;
        }

        await selfAdd({ invitationToken: trimmedToken }).unwrap();
        onJoined?.();
        onClose();
    };

    const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        handleSubmit().catch(() => undefined);
    };

    return (
        <Modal
            preventCloseOnClickOutside
            open={open}
            modalHeading={intl.formatMessage(messages.title)}
            primaryButtonText={intl.formatMessage(messages.submit)}
            secondaryButtonText={intl.formatMessage(sharedMessages.cancel)}
            primaryButtonDisabled={isLoading || !trimmedToken}
            onRequestSubmit={() => { handleSubmit().catch(() => undefined); }}
            onRequestClose={onClose}
            closeButtonLabel={intl.formatMessage(sharedMessages.close)}
        >
            {isLoading && <Loading />}
            <form onSubmit={handleFormSubmit} className={styles.form}>
                <TextInput
                    id="join-existing-parish-code"
                    labelText={intl.formatMessage(messages.codeLabel)}
                    placeholder={intl.formatMessage(messages.codePlaceholder)}
                    value={invitationToken}
                    onChange={(event) => { setInvitationToken(event.currentTarget.value); }}
                    autoComplete="off"
                />
            </form>
        </Modal>
    );
};

const messages = defineMessages({
    title: {
        id: 'src.Screens.Dashboard.InitialSetup.JoinExistingParishModal.title',
        defaultMessage: 'Dołącz do istniejącej parafii',
    },
    codeLabel: {
        id: 'src.Screens.Dashboard.InitialSetup.JoinExistingParishModal.codeLabel',
        defaultMessage: 'Kod zaproszenia',
    },
    codePlaceholder: {
        id: 'src.Screens.Dashboard.InitialSetup.JoinExistingParishModal.codePlaceholder',
        defaultMessage: 'Wpisz kod parafii',
    },
    submit: {
        id: 'src.Screens.Dashboard.InitialSetup.JoinExistingParishModal.submit',
        defaultMessage: 'Dołącz',
    },
});

export default JoinExistingParishModal;
